import { ArrowClockwise, Copy } from '@phosphor-icons/react';
import type { AssetGenerationDto } from '@reelplicate/api-client/model';
import {
  IMAGE_MODELS,
  type ImageModel,
  VIDEO_FORMATS,
  VIDEO_MODELS,
  VIDEO_QUALITIES,
  type VideoFormat,
  type VideoModel,
  type VideoQuality,
} from '@reelplicate/flows';
import { Button, Inline, Stack, toast } from '@reelplicate/ui';
import { useId } from 'react';
import { Link } from 'react-router';

import type { Asset, Scene } from './types';

/** "Kling · 9:16 · 1080p · 5 s · ChatGPT image" */
function settingsLine(g: AssetGenerationDto): string {
  return [
    g.videoModel && (VIDEO_MODELS[g.videoModel as VideoModel]?.label ?? g.videoModel),
    g.format && (VIDEO_FORMATS[g.format as VideoFormat]?.label ?? g.format),
    g.quality && (VIDEO_QUALITIES[g.quality as VideoQuality]?.label ?? g.quality),
    g.durationSeconds && `${g.durationSeconds} s`,
    g.imageModel &&
      `${IMAGE_MODELS[g.imageModel as ImageModel]?.label ?? g.imageModel} image`,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * How a generated image or video was made: the settings and every prompt as
 * sent, to copy - and, for a redrawn scene video whose reel is still here,
 * Make again with all of it filled in.
 */
export function GenerationSection({
  generation,
  assets,
  onMakeAgain,
}: {
  generation: AssetGenerationDto;
  /** The library, to find the reel it remakes. */
  assets: Asset[];
  onMakeAgain: (reel: Asset, scene: Scene) => void;
}) {
  const source = generation.source;
  const reel = source ? assets.find((a) => a.id === source.assetId) : undefined;
  const scene = reel?.scenes?.find((s) => s.scene === source?.scene);
  // The Replicate window redraws the frame; a persona shot can't be remade there.
  const canMakeAgain =
    scene !== undefined && generation.videoModel !== undefined && !generation.imagePrompt;
  const settings = settingsLine(generation);
  return (
    <section className="flex flex-col gap-2">
      <Inline gap={2} alignY="center">
        <h3 className="text-label-md text-text-primary flex-1">How it was made</h3>
        {canMakeAgain && reel && scene && (
          <Button
            size="sm"
            variant="outline"
            leadingIcon={ArrowClockwise}
            onClick={() => onMakeAgain(reel, scene)}
          >
            Make again
          </Button>
        )}
      </Inline>
      <Stack gap={2}>
        {settings && <p className="text-caption text-text-secondary">{settings}</p>}
        {reel && source && (
          <Link
            to={`/media?asset=${reel.id}`}
            className="text-caption text-text-brand w-fit hover:underline"
          >
            Made from scene {source.scene} of {reel.title}
          </Link>
        )}
        {generation.imagePrompt && (
          <PromptBlock label="Persona shot" text={generation.imagePrompt} />
        )}
        {generation.framePrompt && (
          <PromptBlock label="First frame" text={generation.framePrompt} />
        )}
        {generation.prompt && <PromptBlock label="Motion" text={generation.prompt} />}
      </Stack>
    </section>
  );
}

/** One prompt as sent, with Copy. */
function PromptBlock({ label, text }: { label: string; text: string }) {
  const id = useId();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} prompt copied`);
    } catch {
      // No clipboard (plain http, old browsers): select it to copy by hand.
      const node = document.getElementById(id);
      if (node) window.getSelection()?.selectAllChildren(node);
      toast.info(`${label} prompt selected - copy it from the menu`);
    }
  };
  return (
    <div className="bg-bg-subtle flex flex-col gap-1 rounded-[10px] p-3">
      <Inline gap={2} alignY="center">
        <span className="text-caption text-text-tertiary flex-1">{label}</span>
        <Button
          size="sm"
          variant="ghost"
          leadingIcon={Copy}
          aria-label={`Copy ${label.toLowerCase()} prompt`}
          onClick={() => void copy()}
        >
          Copy
        </Button>
      </Inline>
      <p id={id} className="text-body-sm text-text-primary whitespace-pre-line">
        {text}
      </p>
    </div>
  );
}
